/**
 * RedactionPanel — Canvas SuperDoc (présentation pure).
 *
 * **Où il est monté** : dans la coque, par `CanvasResidence`, à partir de la
 * première ouverture du canvas (#856). Aller consulter un dossier ne détruit
 * donc plus le document sélectionné. Changer de scope documentaire ou de
 * compte le remonte explicitement ; les ressources se libèrent par le scope,
 * pas en devinant la destination depuis un démontage React.
 *
 * Toute la logique de streaming (SSE research/generation, accumulation des
 * blocs, auto-ouverture du canvas sur 1er token, notification du back en fin
 * de stream) vit dans `useRedactionStreamingState`, monté sans condition par
 * `CanvasResidence`. Ce composant reçoit donc son état entièrement via props et
 * n'a pas de hooks SSE — c'est ce qui permet à l'auto-ouverture « au 1er bout
 * de texte streamé » de marcher avant que le panneau existe.
 *
 * Le canvas SuperDoc lui-même reste lazy (~1.7 MB) — chargé au premier montage
 * de ce composant, donc à la première ouverture du canvas dans l'onglet.
 */

import * as React from 'react';
import { FilePlus2, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { cleDeMontage } from '@hector/shared-types';
import type { RedactionStreamingState } from '../streaming/useRedactionStreamingState';
import { selectResidentTemplateOverride } from '../state/canvasResidentSeed';
import {
  brouillonDuDocument,
  deciderLeMetaDuDocument,
  libelleDuBandeau,
} from '../state/canvasDocumentMeta';
import { nommerLeDocumentSiNecessaire } from '../state/canvasNommageAutomatique';
import {
  CANVAS_REPLACEMENT_BLOCKED_HINT,
  CANVAS_REPLACEMENT_BLOCKED_MESSAGE,
  useCanvasSessionProtectedByWork,
} from '../state/canvasSessionProtection';
import { useCanvasSessionWritingByAgent } from '../state/canvasAgentWriting';
import { useCanvasAutoSave } from '../hooks/useCanvasAutoSave';
import { CanvasEcritureBandeau } from './CanvasEcritureBandeau';
import { CanvasRestaurationIndisponibleBandeau } from './CanvasRestaurationIndisponible';
import { useRestaurationIndisponible } from '../state/canvasRestaurationIndisponible';
import { CanvasOpenMenu } from './CanvasOpenMenu';
import { CanvasDepartChoixVoile } from './CanvasDepartChoixVoile';
import { CanvasTitle } from './CanvasTitle';
import { SaveToCaseDialog } from './SaveToCaseDialog';
import { CanvasDownloadMenu } from './CanvasDownloadMenu';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { chargerLEditeurDeRedaction } from '../editor/chargerLEditeur';
import { renommerLeDocumentDuResident } from '../state/canvasResidentPresentation';
import { decrireLeRefusDeSortie, ouvrirUnDocumentVierge } from '../state/canvasBasculeDeBrouillon';
import { proposerLeChoixDeDepart } from '../state/canvasChoixDeDepart';
import {
  useCanvasDocumentMountEpoch,
  useCanvasDocumentPhysicalResources,
  useCanvasDocumentPresentation,
  useCanvasDocumentScope,
} from '../state/canvasDocumentScopeContext';

// SuperDoc = canvas par défaut depuis PR4 (le feature flag `useSuperDocRedactionAccess`
// de PR1 a été retiré). Lazy : ~1.7 MB gz chargé seulement quand un job streame.
// Le DEBT-316 (cleanup Plate complet : suppression deps + renames + bundle assertion)
// est marqué résolu côté code ; reste à câbler la License CI gate avant prod.
/**
 * Le bouton en icône seule doit nommer immédiatement son action (#853), sans
 * transformer son infobulle et son nom accessible en notice. La destination
 * présélectionnée reste visible dans le sélecteur de la modale.
 */
const SAVE_TO_CASE_LABEL = 'Enregistrer ce document dans le dossier.';

/**
 * Ce qui s'ajoute à l'infobulle des deux **sorties du document** pendant une activité
 * (#855) — jamais au nom accessible du bouton, qui est son identité (#853).
 *
 * Les deux sorties sont gelées ensemble parce qu'elles produisent le même objet :
 * un `.docx` figé, sorti du canvas. En tirer un pendant qu'Hector écrit rend un
 * acte tronqué **qui ne se signale pas comme tel** — l'avocat le verse au dossier
 * ou le dépose en croyant tenir le document fini. Attendre la fin du tour coûte
 * quelques secondes ; ne pas attendre coûte une pièce fausse.
 */
const CANVAS_SAVE_BLOCKED_HINT = ' : indisponible pendant la rédaction, car le document serait incomplet';

const RedactionCanvasLazy = React.lazy(() =>
  chargerLEditeurDeRedaction().then((m) => ({
    default: m.RedactionCanvas,
  })),
);

interface RedactionPanelProps {
  /** État du streaming canvas — produit par `useRedactionStreamingState`. */
  state: RedactionStreamingState;
  /** Dossier de la conversation active, utilisé seulement comme valeur initiale du sélecteur "Ouvrir". */
  selectedCaseId?: string | null;
  /**
   * LE PORTEUR : la conversation à qui appartient le document ouvert — pas
   * forcément celle qui est affichée. Les deux coïncidaient tant que le canvas
   * vivait dans l'écran assistant ; elles divergent depuis #856, dès que
   * l'avocat va consulter un dossier.
   *
   * ⚠️ **Il n'est plus nullable** (#1336) : depuis que la conversation EST
   * l'identité du document logique, un résident sans porteur ne désignerait
   * rien — `CanvasSessionSnapshot.conversationId` a cessé d'être nullable au
   * lot N, et `CanvasResidence` ne rend un panneau que par session enregistrée.
   * Les `?? null` qui en descendaient neutralisaient silencieusement les gardes
   * de propriétaire en aval, qui traitent `null` comme « pas de contrainte ».
   */
  conversationId: string;
  /** Ce résident est-il celui que l'écran affiche ? Décide des surfaces uniques. */
  isVisibleResident?: boolean;
  /** Masque le panneau canvas sans supprimer ni démonter le document ouvert. */
  onClose?: () => void;
  /**
   * Arrête le tour d'agent qui retient le canvas (#855), uniquement depuis le
   * bandeau explicite. La croix masque toujours et ne connaît pas ce callback.
   */
  onArreterRedaction?: () => void;
}

/**
 * Bouton « Télécharger » du header canvas — SEULE porte de sortie du document
 * jusqu'ici (audit métier 2026-07-30) : l'avocat pouvait rédiger un acte complet
 * sans aucun moyen de le récupérer pour le déposer ou l'envoyer.
 *
 * Réutilise l'export SuperDoc déjà appelé par l'auto-save (`useCanvasAutoSave`),
 * mais avec `triggerDownload:false` + ancre maison : le téléchargement natif de
 * SuperDoc nomme le fichier lui-même, sans le titre de l'acte — un avocat qui
 * télécharge trois actes se retrouverait avec trois fichiers indistinguables.
 */
/**
 * Bouton « Nouveau » du header canvas. Il REMPLACE le document affiché par une
 * page blanche, sans rien détruire depuis le 2026-09-22 : le document courant
 * est enregistré avant la bascule et reste dans « Documents de cette
 * conversation ». Les actions « tout accepter » /
 * « tout rejeter » du suivi des modifications vivent dans la barre d'outils
 * SuperDoc (`TrackChangesToolbarControls`, rendu par RedactionCanvas —
 * déplacées du header le 2026-07-13, demande produit).
 */
function CanvasNewDocumentButton({
  conversationId,
  gele,
}: { conversationId: string; gele: boolean }): React.JSX.Element {
  // ⚠️ **« Nouveau » ne supprime plus le document courant** (2026-09-22) : il se
  // retrouve dans « Documents de cette conversation », et sa suppression est un
  // geste explicite, à la corbeille. Tout l'ordre du geste — enregistrer le
  // sortant, vérifier le plafond, PUIS prendre le bail — vit dans
  // `ouvrirUnDocumentVierge`, jugé sur le vrai runtime : c'est la rencontre du
  // bail et du contrôleur qui portait le défaut du 2026-09-23.
  //
  // Le porteur passé est celui du PANNEAU, pas `scope.conversationId` (#1336) :
  // la capture de la cible le revalide contre le propriétaire vivant du scope.
  const ouvrirLeDocumentVierge = React.useCallback(async () => {
    const issue = await ouvrirUnDocumentVierge(conversationId);
    switch (issue.issue) {
      case 'vierge':
        toast.success('Nouveau document vierge ouvert');
        // La page neuve est montée sur le papier à en-tête par défaut ; l'avocat peut en choisir
        // un autre sur le voile, qui ne rend rien quand le cabinet n'en a déposé aucun.
        proposerLeChoixDeDepart(conversationId);
        return;
      case 'protege':
        toast.error(CANVAS_REPLACEMENT_BLOCKED_MESSAGE);
        return;
      case 'montage-remplace':
        toast.error('Le canvas a changé pendant la création du document vierge. Réessaie depuis la conversation concernée.');
        return;
      case 'refus-de-sortie':
        toast.error(decrireLeRefusDeSortie(issue.refus));
    }
  }, [conversationId]);

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            // Même refus que les deux sorties (#855) : `aria-disabled` + garde
            // dans le gestionnaire, jamais `disabled` — un bouton désactivé ne
            // reçoit aucun événement de pointeur, donc l'infobulle qui porte la
            // raison ne s'ouvrirait jamais.
            className="h-8 w-8 shrink-0 aria-disabled:opacity-60 aria-disabled:cursor-default"
            // Plus de confirmation depuis le 2026-09-22 : elle annonçait « ses
            // modifications non sauvegardées seront perdues », ce qui n'est plus
            // vrai. Le document courant est enregistré avant la bascule et reste
            // dans « Documents de cette conversation » ; il n'y a donc plus rien
            // à confirmer, et une confirmation qui ne protège de rien n'est que
            // de la friction.
            onClick={() => { if (!gele) void ouvrirLeDocumentVierge(); }}
            aria-disabled={gele}
            aria-label="Ouvrir un document vierge dans le canvas"
          >
            <FilePlus2 className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {`Ouvrir un document vierge dans le canvas${gele ? CANVAS_REPLACEMENT_BLOCKED_HINT : ''}`}
        </TooltipContent>
      </Tooltip>
    </>
  );
}

/**
 * La croix du bandeau — **masquer** le canvas, sans interrompre Hector (#1051).
 *
 * Elle dit « masquer » et non « fermer » depuis le 2026-08-13, parce que c'est
 * ce qu'elle fait : le document reste ouvert, garde son porteur et son brouillon,
 * et reparaît tel quel au retour dans sa conversation. Aucun geste ne *ferme* un
 * document — « Nouveau » le remplace, ce qui est autre chose et se confirme.
 *
 * Un document masqué continue volontairement à recevoir ses blocs. L'arrêt est
 * une action destructive distincte et libellée dans `CanvasEcritureBandeau`.
 */
function CanvasCloseButton({ onClose }: { onClose: () => void }): React.JSX.Element {
  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8 shrink-0"
      onClick={onClose}
      title="Masquer le canvas"
      aria-label="Masquer le canvas"
    >
      <X className="h-4 w-4" />
    </Button>
  );
}

export function RedactionPanel({
  state, selectedCaseId, conversationId, isVisibleResident = true, onClose, onArreterRedaction,
}: RedactionPanelProps): React.JSX.Element {
  const scope = useCanvasDocumentScope();
  const { instance } = useCanvasDocumentPhysicalResources();
  const {
    blocks,
    phase,
    headerLabel,
    jobId,
    documentTitle: documentTitleDuJob,
  } = state;
  /**
   * Le document de CETTE conversation, observé directement (#1333).
   *
   * Il n'y a plus de projection d'écran à arbitrer : le panneau parqué et le
   * panneau visible lisent chacun leur propre scope, donc aucun droit de
   * lecture ne dépend plus de ce que l'écran affiche.
   */
  const presentation = useCanvasDocumentPresentation();
  const { templateOverride } = presentation;
  // Titre, nature et provenance viennent d'UNE règle, partagée avec le contexte
  // agent et l'enregistrement du brouillon (#1333). Le titre du job est passé
  // ici plutôt que relu sur le hub : le panneau tient déjà l'écoute affichée,
  // et deux lectures du même fait pourraient différer d'un rendu.
  const meta = deciderLeMetaDuDocument({
    presentation,
    conversationId,
    titreDuTravail: documentTitleDuJob,
    aUnTravailDeRedaction: phase !== 'idle',
  });
  const scopedTemplateMeta = meta.templateMeta;
  const exactDraftId = meta.restoredDraftId ?? scope.getDraftController().getSnapshot().draftId;
  const scopedTemplateOverride = selectResidentTemplateOverride({
    templateOverride,
    // La neutralisation d'un gabarit venu d'une autre conversation est décidée
    // une seule fois, dans `canvasDocumentMeta`, et son VERDICT est lu ici : un
    // `templateMeta` refusé n'y survit pas, et son blob ne doit pas être monté
    // non plus. Retester `scopedTemplateMeta === null` confondrait « aucun
    // gabarit » et « gabarit d'autrui », donc monterait le second.
    templateIsHeldHere: !meta.gabaritEtranger,
    hydratedFromExactDraft: exactDraftId !== null
      && scope.wasColdHydratedFromDraft(exactDraftId),
  });

  /**
   * LE PORTEUR — la conversation à qui appartient CE document. Il arrive ici tel
   * quel, depuis la session que `CanvasResidence` rend : ce panneau ne le
   * calcule pas et ne le publie pas.
   *
   * Il valait « la conversation regardée » tant que le canvas était monté par
   * l'écran assistant (avant #856). Depuis, le canvas réside dans la coque et
   * survit à la navigation : son montage ne dit plus rien de ce qui est affiché,
   * et c'est le fil confirmé qui a ouvert le document qui le possède (#1331).
   */
  const holderConversationId = conversationId;
  /**
   * Un tour d'agent rédige-t-il dans CE document ? (#855)
   *
   * Lu sur la session exacte : le document peut être parqué hors écran pendant
   * qu'Hector écrit. L'écran ne décide ni cette protection ni la destination
   * d'une écriture.
   */
  const documentProtected = useCanvasSessionProtectedByWork(scope.conversationId);
  /**
   * Le bandeau répond à « Hector modifie-t-il CE document ? », pas à « un tour
   * une activité quelque part ? ». Les compteurs exacts couvrent à la fois
   * les blocs streamés (`writer`) et les actions ponctuelles (`mutation`) et
   * retombent indépendamment quand A et B finissent dans n'importe quel ordre.
   */
  const ecritureAgent = useCanvasSessionWritingByAgent(scope.conversationId);
  // Le verdict de restauration est indexé par CONVERSATION, pas par session
  // documentaire : il parle précisément d'un document qui n'a pas pu en obtenir
  // une. Borné au résident visible — un panneau parqué le rendrait hors écran,
  // donc à personne, tout en le dédoublant dans le DOM.
  const verdictDeRestauration = useRestaurationIndisponible(conversationId);
  const restaurationIndisponible = isVisibleResident ? verdictDeRestauration : null;
  const titreDocument = meta.titre;
  const displayedHeaderLabel = libelleDuBandeau(meta, headerLabel);
  const [saveToCaseOpen, setSaveToCaseOpen] = React.useState(false);
  const mountEpoch = useCanvasDocumentMountEpoch();
  // La clé React de l'ÉDITEUR — pas celle du résident, qui est la conversation
  // nue (`CanvasResidence`). Depuis #1337, l'ÉPOQUE DE MONTAGE en est la seule
  // horloge : tout remontage runtime (remplacement, page vierge, admission d'un
  // travail qui remplace le document, réhydratation) alloue une époque, donc
  // reconstruit SuperDoc ; un nouveau travail AFFICHÉ, lui, ne le fait plus —
  // l'admission a remplacé le document avant (`state/canvasAdmission.ts`).
  // `cleDeMontage`, jamais une composition à la main : c'est la forme que le DOM
  // (`data-canvas-host`) et le courtier backend emploient aussi.
  //
  // ⚠️ La part « document » reste, mais plus aucun chemin connu ne la fait
  // varier sans époque neuve : son dernier cas — un brouillon restauré publié
  // sur un scope déjà monté — a disparu avec #1586, la restauration refusant
  // désormais un fil dont le document est monté. Elle est gardée en filet : si
  // une pose de gabarit sans remontage réapparaissait, SuperDoc serait encore
  // reconstruit au lieu de garder l'ancien document à l'écran.
  const redactionCanvasKey = `${cleDeMontage({
    forme: 'conversation',
    conversationId: scope.conversationId,
    mountEpoch,
  })}:${scopedTemplateMeta ? `document-${scopedTemplateMeta.documentId ?? 'sans-identifiant'}` : 'vierge'}`;

  // ⚠️ Le dernier effet documentaire du panneau — le reset au changement de
  // conversation — a disparu avec la projection globale qu'il nettoyait (#1333).
  // Il n'y a plus de valeur partagée qu'un fil pourrait hériter d'un autre :
  // chaque document porte la sienne dans son scope, et un panneau ne change
  // jamais de scope (`CanvasResidence` keye ses résidents par conversation).

  const autosave = useCanvasAutoSave({
    instance,
    // Le PORTEUR, pas la conversation affichée : c'est sous cette identité que
    // le brouillon s'enregistre, et c'est elle qui doit voyager avec l'export.
    conversationId: holderConversationId,
    // Titre, nature, dossier, pièce d'origine et brouillon durable sont dérivés
    // du MÊME meta que le bandeau et le contexte agent (#1333). La dérivation
    // vit dans `canvasDocumentMeta` et non dans ces props : ici, elle était hors
    // de portée de toute garde alors qu'elle décide sous quel nom et dans quel
    // dossier le travail de l'avocat est enregistré.
    ...brouillonDuDocument(meta, selectedCaseId ?? null),
  });
  const saveStatus = autosave.status;

  /**
   * Un document que personne n'a nommé se nomme tout seul, à partir de son
   * contenu — sans quoi la liste des documents d'un fil n'affiche que des
   * « Document sans titre » indiscernables.
   *
   * Déclenché ici et pas dans `RedactionCanvas` parce que la décision tient au
   * titre EFFECTIF (`meta.titre`), qui agrège renommage, métadonnées du
   * document chargé et titre du job : le canvas ne connaît que le dernier
   * maillon. Rythmé sur la version du contrôleur, donc sur les frappes réelles
   * — `capturePresentation` déclenche un rendu, mais pas une version.
   *
   * ⚠️ Jamais pendant qu'un job rédige : l'acte tient son nom du titre validé
   * au lancement, et le document est alors en cours de construction.
   */
  const documentDejaNomme = meta.titre !== null;
  const jobEnCours = phase !== 'idle';
  React.useEffect(() => {
    if (documentDejaNomme || jobEnCours || autosave.version === 0) return;
    // Aucun nettoyage à poser : la lecture est asynchrone, mais le module
    // revalide scope ET instance après l'attente et n'écrit rien si le montage
    // a changé — un drapeau de démontage n'ajouterait aucune garantie.
    void nommerLeDocumentSiNecessaire(holderConversationId).catch(() => undefined);
  }, [documentDejaNomme, jobEnCours, autosave.version, holderConversationId]);
  const documentRuntimeIdentity = React.useMemo(
    /**
     * Ce que le panneau ATTEND du montage sous lui — `RedactionCanvas` le
     * confronte au scope réellement fourni et refuse d'écrire s'ils divergent.
     *
     * Le porteur y est à la fois la moitié de l'identité attendue (#1336 : la
     * conversation EST le document logique) et le **déclencheur
     * d'invalidation** de ce mémo. Ne pas le retirer d'ici : sur un canvas
     * généré ou blanc, rien d'autre de cette identité ne change au passage
     * d'une conversation à l'autre — le document y reste nul. Sans cette
     * invalidation, la session reste `ready` et l'agent de la nouvelle
     * conversation peut inspecter ET MUTER le document resté à l'écran, qui
     * appartient à la précédente. Le fait que la session reparte alors en
     * `loading` sans jamais revenir est un défaut connu — il refuse les
     * requêtes agent au lieu de les laisser viser le mauvais document, ce qui
     * est le bon côté sur lequel échouer. Sa réparation est le sujet de #857.
     */
    () => scopedTemplateMeta
      ? {
          holderConversationId,
          documentId: scopedTemplateMeta.documentId,
        }
      : {
          holderConversationId,
          // Canvas GÉNÉRÉ (plan/acte streamé, page blanche) : identité
          // synthétique avec documentId null — sans elle, aucune session
          // runtime n'était montée, `canvas_document_state` n'était jamais
          // annoncé, et le broker backend refusait canvas_inspect
          // (`CANVAS_SESSION_REQUIRED`) alors que le plan était visible dans
          // le canvas (bug 2026-07-13). Le broker accepte documentId null
          // par design (« un canvas généré n'a pas encore de document
          // backend »).
          documentId: null,
        },
    [holderConversationId, scopedTemplateMeta],
  );

  return (
    <div className="h-full flex flex-col bg-stone-50">
      <div className="px-4 py-2 text-xs text-stone-600 flex items-center gap-2 border-b border-stone-200 bg-white">
        {onClose && (
          <CanvasCloseButton onClose={onClose} />
        )}
        <CanvasTitle
          displayedLabel={displayedHeaderLabel}
          customTitle={titreDocument}
          onRename={(title) => {
            // Le renommage s'écrit dans le document, là où il est LU (#1333) :
            // le bandeau, le contexte agent et le PUT du brouillon lisent tous
            // cette présentation. `markDirty` reste séparé — sans lui, un
            // renommage seul ne survivrait pas à la fermeture de l'onglet.
            renommerLeDocumentDuResident({ conversationId, titre: title });
            scope.getDraftController().markDirty();
          }}
        />
        {/* `ml-auto` : le titre ne pousse plus les actions à droite depuis qu'il
            a une base fondée sur son contenu (#853) — c'est cette marge qui
            absorbe désormais la place libre. */}
        <span
          className="ml-auto text-xs text-stone-500 select-none shrink-0"
          aria-live="polite"
          data-testid="canvas-save-indicator"
        >
          {saveStatus === 'unsaved' && (
            <span>Modifications appliquées, non sauvegardées</span>
          )}
          {saveStatus === 'saving' && (
            <span>Sauvegarde en cours</span>
          )}
          {saveStatus === 'saved' && (
            <span>Modifications sauvegardées</span>
          )}
          {saveStatus === 'error' && (
            <span className="inline-flex items-center gap-1 text-amber-600">
              {autosave.failureMessage ?? 'Le brouillon n’a pas pu être sauvegardé'}
              <button
                type="button"
                className="underline underline-offset-2 hover:text-amber-800"
                onClick={() => { void scope.getDraftController().checkpoint().catch(() => undefined); }}
              >
                Réessayer
              </button>
            </span>
          )}
        </span>
        {/* Région live PERMANENTE, vide au repos (#855). Elle doit préexister au
            changement qu'elle annonce : un nœud inséré en même temps que son
            texte n'est pas énoncé de façon fiable, et l'avocat non-voyant
            verrait ses commandes se figer sans jamais apprendre pourquoi. */}
        <span className="sr-only" aria-live="polite" data-testid="canvas-activity-announcement">
          {/* Ce qu'elle annonce a changé avec le bandeau (2026-08-13) : le
              document n'est plus rendu inaccessible, donc dire qu'il ne l'est
              pas serait faux — et un avocat non-voyant qui renonce à écrire sur
              la foi d'une annonce périmée perd une capacité qu'il avait. */}
          {ecritureAgent ? 'Hector modifie ce document. Mieux vaut ne pas y écrire en même temps.' : ''}
        </span>
        {/* Actions en icônes seules (#853, arbitrage D4 du prototype #826) : en
            toutes lettres elles laissaient une centaine de pixels au titre, qui
            se tronquait — et toute attribution ajoutée était absorbée sans même
            s'afficher. « Ouvrir » garde son libellé : c'est le seul qui déplie
            un menu, et un chevron nu ne dit pas ce qu'il ouvre. */}
        <TooltipProvider delayDuration={300}>
          <div className="flex items-center gap-1 shrink-0">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 aria-disabled:opacity-60 aria-disabled:cursor-default"
                  // Garde DANS le gestionnaire, jamais `disabled` : un bouton
                  // désactivé ne reçoit aucun événement de pointeur, donc son
                  // infobulle — le seul endroit où la raison du gel est écrite —
                  // ne s'ouvrirait jamais (#853).
                  onClick={() => { if (!documentProtected) setSaveToCaseOpen(true); }}
                  aria-disabled={documentProtected}
                  aria-label={SAVE_TO_CASE_LABEL}
                >
                  <Save className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {`${SAVE_TO_CASE_LABEL}${documentProtected ? CANVAS_SAVE_BLOCKED_HINT : ''}`}
              </TooltipContent>
            </Tooltip>
            <CanvasDownloadMenu fileLabel={displayedHeaderLabel} gele={documentProtected} />
            {/* Les commandes qui REMPLACENT le document sont gelées comme les
                deux sorties : elles détruiraient l'acte en cours d'écriture (#893). */}
            <CanvasNewDocumentButton conversationId={conversationId} gele={documentProtected} />
            <CanvasOpenMenu
              initialCaseId={selectedCaseId}
              conversationId={holderConversationId}
              gele={documentProtected}
            />
          </div>
        </TooltipProvider>
      </div>
      {/* Le bandeau d'écriture est EN FLUX, avant la zone d'édition (#855 refondu
          le 2026-08-13) : il pousse le document au lieu de le recouvrir, parce
          que l'avocat demande une rédaction précisément pour la voir arriver.
          Il suit l'écriture constatée, pas l'occupation du pool — voir `CanvasEcritureBandeau`. */}
      {ecritureAgent && <CanvasEcritureBandeau onArreter={onArreterRedaction} />}
      {/* L'avocat qui ouvre son Canvas à la main après un échec de restauration
          obtiendrait sinon un éditeur vierge MUET, et sa première frappe
          créerait un brouillon concurrent de celui qu'on n'a pas su lire (US 20).
          ⚠️ Le refus est ici EN FLUX et ne remplace RIEN. À la place de
          `RedactionCanvas`, il a produit un interblocage : SuperDoc ne se
          construisait pas, la session n'atteignait jamais `ready`, le chargement
          qui l'attendait expirait — 25 parcours agent rouges d'un coup, mesuré le
          2026-09-09. Rendu avant le conteneur de défilement, comme le bandeau
          d'écriture : rien de positionné ne doit entrer dans `RedactionCanvas`,
          qui porte le `compactMeasurementSelector` du peintre. */}
      {restaurationIndisponible && (
        <CanvasRestaurationIndisponibleBandeau etat={restaurationIndisponible} />
      )}
      {/* `relative` : le voile de choix du papier à en-tête se pose en absolu sur cette zone,
          à côté de `RedactionCanvas` — jamais à sa place ni dedans (les deux interdits du
          commentaire au-dessus valent pour lui). */}
      <div className="relative flex-1 overflow-y-auto">
        <React.Suspense
          fallback={
            <div className="p-8 text-center text-sm text-stone-500">
              Chargement du canvas…
            </div>
          }
        >
          <RedactionCanvasLazy
            key={redactionCanvasKey}
            initialBlocks={blocks}
            liveStreaming={phase === 'streaming'}
            jobId={jobId}
            templateOverride={scopedTemplateOverride}
            documentRuntimeIdentity={documentRuntimeIdentity}
          />
        </React.Suspense>
        <CanvasDepartChoixVoile conversationId={conversationId} />
      </div>
      <SaveToCaseDialog
        open={saveToCaseOpen}
        onOpenChange={setSaveToCaseOpen}
        caseId={selectedCaseId ?? null}
        defaultName={displayedHeaderLabel}
      />
    </div>
  );
}
